<template>
<!-- eslint-disable max-len -->
<div
  v-if="starfighter"
  class="narrator-panel sf"
  :style="style"
>
  <span class="image-text">
    <img v-if="icon" :src="resolveAssetPath(icon, 'characters')">
    <span class="message" :class="{ 'no-image': !icon }"><slot></slot></span>
  </span>

  <nameplate
    :character="char ? char : narrator"
    :player="isPlayer"
  />

  <transition enter-active-class="animated fadeIn">
    <div
      v-if="to || next || back"
      style="
        flex: 100px 0 0;
        display: flex;
        flex-flow: row nowrap;
        justify-content: center;
        align-items: center;
      "
      :style="buttonContainerStyle"
    >
      <div class="navigate-button-wrapper">
        <button
          v-if="back"
          class="btn back-btn"
          :style="backButtonStyle"
          @click.prevent="onBack"
          v-b-tooltip.hover.bottomright
          :title="
            (narrative && narrative.backButtonText)
              ? narrative.backButtonText
              : ((game && game.backButtonDefaultText) ? game.backButtonDefaultText : 'Back')
          "
        >
          <!-- <slot name="back">
            <span v-html="(narrative && narrative.backButtonText) ? narrative.backButtonText : ((game && game.backButtonDefaultText) ? game.backButtonDefaultText : 'Back')"></span>
          </slot> -->
        </button>
      </div>
      <div class="navigate-button-wrapper">
        <button
          v-if="to || next"
          class="btn next-btn"
          :style="buttonStyle"
          @click.prevent="navigate"
          v-focus="!noFocus"
        >
          <slot name="button">
            <span
              v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"
              :style="buttonContentStyle"
            ></span>
          </slot>
        </button>
      </div>
    </div>
  </transition>
</div>
<sliced-box
  v-else
  class="narrator-panel native"
  :style="style"
>
  <span class="image-text">
    <img v-if="icon" :src="resolveAssetPath(icon, 'characters')">
    <span class="message" :class="{ 'no-image': !icon }"><slot></slot></span>
  </span>

  <transition enter-active-class="animated fadeIn">
    <button
      v-if="to || next"
      class="btn"
      :style="buttonStyle"
      @click.prevent="navigate"
      v-focus="!noFocus"
    >
      <slot name="button">
        <span
          v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"
          :style="buttonContentStyle"
        ></span>
      </slot>
    </button>
  </transition>
</sliced-box>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import Nameplate from '../ui/Nameplate';
import SlicedBox from '../ui/SlicedBox';

export default {
  components: {
    Nameplate,
    SlicedBox
  },
  props: {
    to: { type: String },
    replace: { type: Boolean },
    next: { type: Function },
    back: { type: Function },
    noFocus: { type: Boolean },
    minHeight: { type: String },
    character: { type: Object },
    noIcon: { type: Boolean },
    player: { type: Boolean }
  },
  computed: {
    ...mapGetters([
      'selectedAvatar',
      'narrator',
      'narrative',
      'narrativeCenterCharacter',
      'game',
      'starfighter',
      'native'
    ]),
    isPlayer() {
      return this.player || (this.narrative && this.narrative.centerPlayer);
    },
    char() {
      return ((this.isPlayer && this.selectedAvatar)
        ? this.selectedAvatar
        : (this.character || this.narrativeCenterCharacter || {})
      );
    },
    hideIcon() {
      if (this.noIcon) return true;
      if (this.narrative != null) {
        if (this.narrative.hideCenterPanelIcon) return true;
        if (this.narrative.showCenterPanelIcon) return false;
      }
      if (this.game.centerSpeechBubbleHideCharacterIcon) return true;
      return false;
    },
    icon() {
      if (this.hideIcon) return '';
      let variantName = '';
      if (this.narrative != null && this.char != null
        && this.char.imageVariants && this.char.imageVariants.length > 0
        && this.narrative.centerVariant) {
        variantName = this.narrative.centerVariant;
      }
      if (variantName) {
        const variant = this.char.imageVariants.find(v => v.name === variantName);
        if (variant) {
          return variant.image;
        }
      }
      return this.char.icon;
    },
    style() {
      let bottomVal = '15px';

      const bubbleWidth = this.game.centerSpeechBubbleWidth || '1540px';
      const totalXoffset = this.narrative.panelXOffset;
      const totalYoffset = this.narrative.panelYOffset;

      const leftVal =
        totalXoffset
          ? `calc(((1600px - ${bubbleWidth}) / 2) + ${totalXoffset})`
          : `calc((1600px - ${bubbleWidth}) / 2)`;

      if (totalYoffset) bottomVal = `calc(${bottomVal} + ${totalYoffset})`;

      return {
        width: bubbleWidth,
        'min-height': (this.minHeight ? this.minHeight : undefined),
        left: leftVal,
        bottom: bottomVal,
        color:
          this.game.speechBubbleTextColor
            ? this.game.speechBubbleTextColor
            : undefined,
        'background-color':
          this.game.speechBubbleBackgroundColor
            ? this.game.speechBubbleBackgroundColor
            : undefined,
        border:
          this.game.speechBubbleBackgroundColor
            ? 'none'
            : undefined,
        'border-radius':
          this.game.speechBubbleBackgroundColor
            ? '24px'
            : undefined,
      };
    },
    buttonContainerStyle() {
      const useAbsolute = !!(this.game.insetNextButtonPositionX);
      return {
        position: useAbsolute ? 'absolute' : undefined,
        'margin-bottom':
          (!useAbsolute && this.game.insetNextButtonPositionY)
            ? this.game.insetNextButtonPositionY
            : undefined,
        bottom:
          (useAbsolute && this.game.insetNextButtonPositionY)
            ? this.game.insetNextButtonPositionY
            : undefined,
        left:
          (!(this.game.insetNextButtonPositionXFromRight) && this.game.insetNextButtonPositionX)
            ? this.game.insetNextButtonPositionX
            : undefined,
        right:
          (this.game.insetNextButtonPositionXFromRight && this.game.insetNextButtonPositionX)
            ? this.game.insetNextButtonPositionX
            : undefined,
      };
    },
    buttonStyle() {
      const buttonWidth =
        this.game.insetNextButtonWidth
          ? this.game.insetNextButtonWidth
          : '279px';
      const topPadding =
        this.game.insetNextButtonWidth
          ? `calc(8 * ${this.game.insetNextButtonWidth} / 279)`
          : '8px';
      const leftPadding =
        this.game.insetNextButtonWidth
          ? `calc(10 * ${this.game.insetNextButtonWidth} / 279)`
          : '10px';
      const rightPadding =
        this.game.insetNextButtonWidth
          ? `calc(42 * ${this.game.insetNextButtonWidth} / 279)`
          : '42px';
      const buttonHeight =
        this.game.insetNextButtonHeight
          ? this.game.insetNextButtonHeight
          : undefined;
      return {
        'min-width': buttonWidth,
        width: buttonWidth,
        'min-height': buttonHeight,
        height: buttonHeight,
        padding: `${topPadding} ${rightPadding} ${topPadding} ${leftPadding}`,
      };
    },
    backButtonStyle() {
      const buttonWidth =
        this.game.insetNextButtonWidth
          ? `calc(84 * ${this.game.insetNextButtonWidth} / 279)`
          : '84px';
      const buttonHeight =
        this.game.insetNextButtonHeight
          ? this.game.insetNextButtonHeight
          : undefined;
      return {
        'min-width': buttonWidth,
        width: buttonWidth,
        'min-height': buttonHeight,
        height: buttonHeight,
      };
    },
    buttonContentStyle() {
      return {
        'font-size': this.game.insetNextButtonFontSize ? this.game.insetNextButtonFontSize : undefined,
      };
    },
  },
  methods: {
    ...mapActions([
      'playSound',
    ]),
    async playClick() {
      await Vue.nextTick();
      this.playSound({
        sound:
          this.game.nextButtonSound
          || this.game.clickSoundOverride
          || this.resolveAssetPath('click_cut.mp3', 'ui/customer'),
        soundIsUnstoppable: true,
      });
    },
    async playBackClick() {
      await Vue.nextTick();
      this.playSound({
        sound:
          this.game.backButtonSound
          || this.game.clickSoundOverride
          || this.resolveAssetPath('click_cut.mp3', 'ui/customer'),
        soundIsUnstoppable: true,
      });
    },
    async navigate() {
      await this.playClick();
      if (this.next) {
        this.next();
      } else if (this.to) {
        if (this.replace) this.$router.replace(this.to);
        else this.$router.push(this.to);
      }
    },
    onBack() {
      this.playBackClick();
      if (this.back) {
        this.back();
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.narrator-panel {
  position: absolute;
  padding: 20px 17px 20px 35px;
  margin: 0;
  font-size: 32px;
  // font-weight: bold;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  min-height: 166px;
}
.narrator-panel.sf {
  background-color: white;
  border: 4px solid white;
  border-radius: 2px;
  z-index: 10;
}
.narrator-panel.native {
  .message {
    margin: -50px -32px;
  }
}
.image-text {
  flex: 100px 1 1;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
}
.narrator-panel img {
  float: left;
  height: 175px;
  margin-right: 50px;
  background-color: var(--narratorIconBackgroundColor);
  flex: 175px 0 0;
}
.narrator-panel .btn {
  font-size: 40px;
  padding: 8px 10px;
  min-width: 280px;
  min-height: 80px;
}
.narrator-panel .next-btn {
  font-size: 40px;
  padding-right: 42px;
  min-width: 279px;
  width: 279px;
  animation: none;
  mask: url('../../../assets/ui/mask2_next.png') center/contain;
  mask-repeat: no-repeat;
}
.narrator-panel .back-btn {
  min-width: 84px;
  width: 84px;
  margin-right: 4px;
  animation: none;
  mask: url('../../../assets/ui/mask2_back.png') center/contain;
  mask-repeat: no-repeat;
}
.navigate-button-wrapper:focus-within {
  animation: focus-anim 2s infinite cubic-bezier(0.445, 0.05, 0.55, 0.95);
  animation-delay: 0.25s;
}
.narrator-panel.native button {
  position: absolute;
  right: -54px;
  bottom: -30px;
}
.message {
  padding-right: 15px;
  flex: 175px 1 1;
}

@keyframes focus-anim {
  0% {
    filter: none;
  }

  50% {
    filter:
      drop-shadow(0px 0px 5px var(--primaryBorderColor))
      drop-shadow(0px 0px 6px var(--primaryBorderColor));
  }

  100% {
    filter:
      drop-shadow(0px 0px 10px transparent)
      drop-shadow(0px 0px 12px transparent);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/NarratorPanel.vue